import { RequestFailedError, TimeoutError, UnauthorizedError } from '@/classes/Error';
import { SECOND_MS } from '@/constants';

const DEFAULT_TIMEOUT_MS = 10 * SECOND_MS;
const UNAUTHORIZED = 401;

interface RequestProps {
  url: string;
  init?: RequestInit;
  timeoutMs?: number;
}

export async function request<T>({ url, init, timeoutMs = DEFAULT_TIMEOUT_MS }: RequestProps): Promise<T | null> {
  // ! AbortController 는 Chromium 66 부터다. (webOS 4.0 = Chromium 53)
  // ! 없는 환경에서도 시간 초과를 알 수 있도록 race 로 재고, 있으면 요청까지 끊는다.
  const controller = typeof AbortController === 'undefined' ? undefined : new AbortController();

  let timedOut = false;
  let timer: ReturnType<typeof setTimeout> | undefined;

  const timeout = new Promise<never>((_, reject) => {
    timer = setTimeout(() => {
      timedOut = true;

      if (controller) {
        controller.abort();
      }

      reject(new TimeoutError(timeoutMs));
    }, timeoutMs);
  });

  try {
    const response = await Promise.race([fetch(url, { ...init, signal: controller && controller.signal }), timeout]);

    if (response.status === UNAUTHORIZED) {
      throw new UnauthorizedError();
    }

    if (!response.ok) {
      throw new RequestFailedError(response.status);
    }

    const body = await Promise.race([response.text(), timeout]);

    return body ? (JSON.parse(body) as T) : null;
  } catch (e) {
    // ! abort 가 유발한 AbortError 가 TimeoutError 보다 먼저 도착할 수 있어 시간 초과로 통일한다.
    // ! 응답이 이미 판정된 뒤(401·에러 상태)라면 그 결과를 그대로 둔다.
    const isResponseError = e instanceof UnauthorizedError || e instanceof RequestFailedError;

    if (timedOut && !isResponseError) {
      throw new TimeoutError(timeoutMs);
    }

    throw e;
  } finally {
    clearTimeout(timer);
  }
}
